import Slider, { Settings } from "react-slick";
import { RouteFactory } from "@app/routePaths";
import Card from "@particles/card/Card";
import { BasicCardProps, CardVariations } from "@particles/card/utils/types";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { IS_PLAYWRIGHT } from "@util/constant";
import { cn } from "@util/helpers";

type BtLuxeCarouselProps = {
  /** Optional className for the component. */
  className?: string;
  /** The props needed for the card. */
  cardProps: BasicCardProps[];
  /** Optional className for the dots. */
  dotsCustomClass: string;
  /** Optional className for the bt luxe class. */
  btLuxeClass?: string;
  /** Optional variations for the card. */
  variations?: CardVariations;
};

const BtLuxeCarousel = ({
  cardProps,
  dotsCustomClass,
  btLuxeClass,
  variations,
}: BtLuxeCarouselProps): React.ReactElement => {
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const isSubscriber = OKTAUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER;

  const SliderCarouselComponent =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    typeof window === "undefined" ? Slider.default : Slider;

  const settings: Settings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: !IS_PLAYWRIGHT,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    swipeToSlide: true,
    adaptiveHeight: true,
    arrows: false,
    dotsClass: cn(
      dotsCustomClass,
      "!flex pt-3 justify-center lg:w-2/4 lg:ml-auto"
    ),
    customPaging: (index: number) => (
      <button
        className="!before:hidden duration-all !m-[5px] !h-2 !w-2 scale-100 !rounded-full bg-gray-550 !p-0 transition-all"
        aria-label={`Slide ${index}`}
      ></button>
    ),
  };
  const defaultVariant: CardVariations = {
    image: {
      position: "left",
      width: "w-full lg:w-1/2",
      extraClass: "object-cover",
      aspectRatio: "aspect-3x2",
      orientation: "landscape",
      filters: {
        breakpoint: "(min-width: 992px)",
        w: 588,
        h: 392,
        dpr: 1,
        f: "webp",
      },
      // [ { breakpoint: "(min-width: 768px)", w: 696, h: 464, dpr: 1, f: "webp" }, { w: 401, h: 267, dpr: 1, f: "webp" }, ],
    },
    content: {
      width: "w-full lg:w-1/2",
      extraClass: "p-4 bg-gray-850 text-white",
      blurb: { extraClass: "mt-2" },
    },
    title: {
      size: "text-xl lg:text-6xl",
      color: "text-white",
      extraClass: "text-left font-pt-serif",
    },
    kicker: {
      color: "text-gray-515",
      extraClass: "uppercase",
    },
  };

  return (
    <div data-testid="lifestyle-carousel-component">
      <SliderCarouselComponent {...settings}>
        {cardProps.map((cardProp) => {
          // const uniqueName = sections?.[0]?.uniqueName ?? "";
          const section = {
            extraClass: cn({
              [cn(
                "bg-transparent bg-no-repeat no-underline text-transparent",
                "max-w-full mb-2.5 block w-[70px] h-[21px] lg:w-[104px] lg:h-[31px]",
                "bg-[url(/src/app/assets/logo-btluxe-white.svg)]",
                btLuxeClass
              )]: cardProp.meta?.to === RouteFactory.lifestyleLuxe,
            }),
          };

          return (
            <Card
              {...cardProp}
              variations={{ ...(variations || defaultVariant), section }}
              key={cardProp.id}
              className="items-stretch"
              displayPaywall={isSubscriber && cardProp.displayPaywall}
            />
          );
        })}
      </SliderCarouselComponent>
    </div>
  );
};

export default BtLuxeCarousel;
